import Button from 'components/button/Button';
import FormWithProvider from 'components/forms/form/Form';
import FormFieldset from 'components/forms/form/FormFieldset';
import TextField from 'components/forms/textField/TextField';
import {
  Integration,
  IntegrationEnum,
} from 'models/InternalIntegrationSetting';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { normalizeKey } from 'utils/localeUtils';
import useAppModal from 'hooks/useAppModal';
import useEnableIntegrationMutation, {
  CustomIntegrationSettingsParams,
} from 'pages/integrations/hooks/useEnableIntegrationMutation';
import { QueryKey } from '@tanstack/react-query';
import ModalFormSubmitButton from 'components/modal/form/ModalFormSubmitButton';
import { IntegrationMutationLevel } from 'pages/integrations/hooks/useDisableIntegrationMutation';
import useRedirectToIntegrationDetails from 'pages/integrations/hooks/useRedirectToIntegrationDetails';
import {
  CUSTOM_INTEGRATION_ACTIVATION_FIELDS,
  CUSTOM_INTEGRATION_ACTIVATION_MODAL_ID,
} from './CustomIntegrationModal.constants';
import {
  CustomIntegrationFormSchemaType,
  getCustomIntegrationFormSchema,
} from './CustomIntegrationModal.schema';
import {
  FieldsContainer,
  HeaderContainer,
} from './CustomIntegrationModal.styles';

const CustomIntegrationModal = ({
  integration,
  queryKey,
}: {
  integration: Integration;
  queryKey: QueryKey;
}) => {
  const { t } = useTranslation();
  const { closeModal } = useAppModal();
  const { enableIntegration, isLoading } = useEnableIntegrationMutation();
  const { redirectToIntegrationDetails } = useRedirectToIntegrationDetails();

  const integrationName = integration.displayName || integration.name;

  const handleCancel = () => {
    closeModal(CUSTOM_INTEGRATION_ACTIVATION_MODAL_ID);
  };

  const handleSubmit = (data: CustomIntegrationFormSchemaType) => {
    const getCustomIntegrationSettings =
      (): CustomIntegrationSettingsParams => {
        switch (integration.enumId) {
          case IntegrationEnum.HELLOSIGN:
            return {
              type: IntegrationEnum.HELLOSIGN,
              apiKey: data.apiKey,
            };
          case IntegrationEnum.TINYPNG:
            return {
              type: IntegrationEnum.TINYPNG,
              apiKey: data.apiKey,
            };
          case IntegrationEnum.MAILCHIMP:
            return {
              type: IntegrationEnum.MAILCHIMP,
              apiKey: data.apiKey,
              listId: data.audienceId,
            };
          case IntegrationEnum.POINTCENTRAL:
            return {
              type: IntegrationEnum.POINTCENTRAL,
              login: data.login,
              // BE uses the password as the api key internally
              apiKey: data.password,
            };
          case IntegrationEnum.VACATIONRENTPAYMENT:
            return {
              type: IntegrationEnum.VACATIONRENTPAYMENT,
              username: data.login,
              password: data.password,
              propertyCode: data.propertyCode,
            };
          default:
            throw new Error('Invalid integration enum');
        }
      };

    enableIntegration(
      {
        level: IntegrationMutationLevel.AGENCY,
        enumId: integration.enumId,
        queryKey,
        customIntegrationSettings: getCustomIntegrationSettings(),
      },
      {
        onSuccess: () => {
          closeModal(CUSTOM_INTEGRATION_ACTIVATION_MODAL_ID);
          redirectToIntegrationDetails(integration);
        },
      },
    );
  };

  return (
    <FormWithProvider
      onSubmit={handleSubmit}
      resolver={getCustomIntegrationFormSchema(integration.enumId)}
      defaultValues={{
        apiKey: '',
        audienceId: '',
        login: '',
        password: '',
        propertyCode: '',
      }}
    >
      <Modal.Header closeButton>
        <HeaderContainer>
          <img
            src={integration.logo}
            alt={integrationName}
            width={48}
            height={48}
          />

          <Modal.Title id="modal-title">
            {t('pageIntegrations.modals.enableCustomIntegration.title', {
              integration: integrationName,
            })}
          </Modal.Title>
        </HeaderContainer>
      </Modal.Header>

      <Modal.Body>
        {t('pageIntegrations.modals.enableCustomIntegration.description', {
          integration: integrationName,
        })}

        <FormFieldset>
          <FieldsContainer>
            {CUSTOM_INTEGRATION_ACTIVATION_FIELDS[
              integration.enumId as keyof typeof CUSTOM_INTEGRATION_ACTIVATION_FIELDS
            ].map((field) => (
              <TextField
                name={field.name}
                label={t(
                  normalizeKey(
                    `pageIntegrations.modals.enableCustomIntegration.fields.${field.name}`,
                  ),
                )}
                key={field.name}
                colSmInput={8}
                colSmLabel={3}
                type={field.fieldType}
                required
              />
            ))}
          </FieldsContainer>
        </FormFieldset>
      </Modal.Body>

      <Modal.Footer>
        <Button
          onClick={handleCancel}
          disabled={isLoading}
          data-testid="cancel-button"
        >
          {t('common.cancel')}
        </Button>

        <ModalFormSubmitButton
          bsStyle="primary"
          disabled={isLoading}
          data-testid="connect-button"
        >
          {t('pageIntegrations.modals.enableCustomIntegration.connect')}
        </ModalFormSubmitButton>
      </Modal.Footer>
    </FormWithProvider>
  );
};

export default CustomIntegrationModal;
